import { Package } from "@/components/icons";
import { Badge, Button } from "@/components/kit";
import { cn } from "@/lib/utils";
import { isMatchedImportLine } from "@/modules/inventory/domain/import-order-line-filters";
import type { ImportOrderPhase13LineDTO } from "@/modules/inventory/domain/types";
import {
  convertReceiveQtyToStock,
  formatQtyWithUnit,
  usesAlternateReceiveUnit,
} from "@/modules/products/domain/unit-conversion";
import {
  formatLakAmount,
  importOrderPriceUnitLabel,
} from "../lib/import-order-labels";
import { ImportOrderPendingBadge } from "./ImportOrderPendingBadge";

type ImportOrderLinesPanelProps = {
  lines: ImportOrderPhase13LineDTO[];
  currency?: string;
  canMatch?: boolean;
  onMatchLine?: (lineId: string) => void;
  onSkipLine?: (lineId: string) => void;
};

function matchedLineStatus(line: ImportOrderPhase13LineDTO): {
  label: string;
  className: string;
} {
  if (line.remainingQuantity === 0 && line.shortQuantity === 0) {
    return {
      label: "ຮັບຄົບ",
      className: "border-leaf/40 bg-leaf/10 text-leaf",
    };
  }
  if (line.remainingQuantity === 0 && line.shortQuantity > 0) {
    return {
      label: "ປິດແລ້ວ",
      className: "border-border bg-muted/50 text-muted-foreground",
    };
  }
  if (line.receivedQuantity === 0 && line.shortQuantity === 0) {
    return {
      label: "ລໍຖ້າຮັບ",
      className: "border-secondary/30 bg-secondary/8 text-secondary",
    };
  }
  return {
    label: "ຮັບບາງສ່ວນ",
    className: "border-primary/40 bg-primary/15 text-foreground",
  };
}

function QtyStat({
  label,
  value,
  unit,
  tone = "default",
}: {
  label: string;
  value: number;
  unit?: string;
  tone?: "default" | "leaf" | "warn" | "muted" | "accent";
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col rounded-xl border px-2.5 py-2 text-center",
        tone === "leaf" && "border-leaf/30 bg-leaf/8",
        tone === "warn" && "border-destructive/25 bg-destructive/6",
        tone === "muted" && "border-border/60 bg-muted/30",
        tone === "accent" && "border-secondary/30 bg-secondary/8",
        tone === "default" && "border-secondary/15 bg-background/70",
      )}
    >
      <span className="text-[10px] text-muted-foreground tracking-wide">
        {label}
      </span>
      <span
        className={cn(
          "font-semibold text-base tabular-nums leading-tight",
          tone === "leaf" && "text-leaf",
          tone === "warn" && value > 0 && "text-destructive",
          tone === "accent" && value > 0 && "text-secondary",
        )}
      >
        {value}
      </span>
      {unit ? (
        <span className="truncate text-[10px] text-muted-foreground">{unit}</span>
      ) : null}
    </div>
  );
}

function ReceiveProgress({
  line,
  receiveUnit,
}: {
  line: ImportOrderPhase13LineDTO;
  receiveUnit: string;
}) {
  const ordered = Math.max(line.orderedQuantity, 1);
  const receivedPct = Math.min(
    100,
    Math.round((line.receivedQuantity / ordered) * 100),
  );
  const shortPct = Math.min(
    100 - receivedPct,
    Math.round((line.shortQuantity / ordered) * 100),
  );

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="text-muted-foreground">ຄວາມຄືບໜ້າຮັບເຂົ້າ</span>
        <span className="font-medium tabular-nums">
          {formatQtyWithUnit(line.receivedQuantity, receiveUnit)}
          <span className="text-muted-foreground">
            /{formatQtyWithUnit(line.orderedQuantity, receiveUnit)}
          </span>
          {line.shortQuantity > 0 ? (
            <span className="text-destructive">
              {" "}
              · ຂາດ {formatQtyWithUnit(line.shortQuantity, receiveUnit)}
            </span>
          ) : null}
        </span>
      </div>
      <div className="flex h-2 overflow-hidden rounded-full bg-muted/60">
        <div
          className="bg-leaf transition-[width]"
          style={{ width: `${receivedPct}%` }}
        />
        {shortPct > 0 ? (
          <div
            className="bg-destructive/70 transition-[width]"
            style={{ width: `${shortPct}%` }}
          />
        ) : null}
      </div>
    </div>
  );
}

function PendingLineCard({
  line,
  index,
  currency,
  canMatch,
  onMatchLine,
  onSkipLine,
}: {
  line: ImportOrderPhase13LineDTO;
  index: number;
  currency: string;
  canMatch?: boolean;
  onMatchLine?: (lineId: string) => void;
  onSkipLine?: (lineId: string) => void;
}) {
  const priceUnit = importOrderPriceUnitLabel(currency);
  const title = line.externalTitle?.trim() || line.productName;

  return (
    <li className="px-4 py-4">
      <div className="flex flex-col gap-3 rounded-xl border border-amber-500/25 bg-amber-500/5 p-3">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-900 dark:text-amber-100">
            <Package className="size-4" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
                #{index + 1}
              </span>
              <ImportOrderPendingBadge status={line.matchStatus} />
            </div>
            <p className="font-medium text-sm leading-snug">{title}</p>
            {line.externalUrl ? (
              <a
                href={line.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-secondary text-xs underline-offset-2 hover:underline"
              >
                ເປີດລິ້ງຈາກແອບ
              </a>
            ) : null}
            {line.skipNote ? (
              <p className="text-muted-foreground text-xs">{line.skipNote}</p>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <QtyStat label="ສັ່ງ" value={line.orderedQuantity} />
          <QtyStat
            label="ລາຄາ"
            value={Number(line.unitPriceForeign)}
            unit={priceUnit}
          />
        </div>

        {line.matchStatus === "pending" && canMatch ? (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="rounded-full"
              onClick={() => onMatchLine?.(line.id)}
            >
              ຈັບຄູ່ SKU
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => onSkipLine?.(line.id)}
            >
              ຂ້າມລາຍການ
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function MatchedLineCard({
  line,
  index,
  currency,
}: {
  line: ImportOrderPhase13LineDTO;
  index: number;
  currency: string;
}) {
  const status = matchedLineStatus(line);
  const priceUnit = importOrderPriceUnitLabel(currency);
  const hasAltUnit = usesAlternateReceiveUnit(line);
  const receiveUnit = line.receiveUnitLabel?.trim() || line.stockUnitLabel;
  const orderedStockQty = convertReceiveQtyToStock(
    line.orderedQuantity,
    line.receiveConversionFactor,
  );

  return (
    <li className="px-4 py-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-secondary">
            <Package className="size-4" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
                #{index + 1}
              </span>
              <Badge className={status.className}>{status.label}</Badge>
            </div>
            <p className="font-medium text-sm leading-snug">{line.productName}</p>
            <p className="text-muted-foreground text-xs">{line.variantName}</p>
            {line.description ? (
              <p className="text-muted-foreground text-xs">{line.description}</p>
            ) : null}
            {hasAltUnit ? (
              <p className="text-muted-foreground text-xs">
                ສັ່ງ {formatQtyWithUnit(line.orderedQuantity, receiveUnit)} →{" "}
                {formatQtyWithUnit(orderedStockQty, line.stockUnitLabel)} ເຂົ້າສະຕັອກ
              </p>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          <QtyStat label="ສັ່ງ" value={line.orderedQuantity} unit={receiveUnit} />
          <QtyStat
            label="ຮັບແລ້ວ"
            value={line.receivedQuantity}
            unit={receiveUnit}
            tone="leaf"
          />
          <QtyStat
            label="ຂາດ"
            value={line.shortQuantity}
            unit={receiveUnit}
            tone={line.shortQuantity > 0 ? "warn" : "muted"}
          />
          <QtyStat
            label="ຄ້າງ"
            value={line.remainingQuantity}
            unit={receiveUnit}
            tone={line.remainingQuantity > 0 ? "accent" : "muted"}
          />
        </div>

        <ReceiveProgress line={line} receiveUnit={receiveUnit} />

        <div className="grid gap-2 rounded-xl border border-secondary/15 bg-background/50 px-3 py-2.5 sm:grid-cols-3">
          <div>
            <p className="text-[10px] text-muted-foreground">
              ລາຄາ/{receiveUnit} ({priceUnit})
            </p>
            <p className="font-medium text-sm tabular-nums">
              {Number(line.unitPriceForeign).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">
              Landed/{receiveUnit} (LAK)
            </p>
            <p className="font-medium text-sm tabular-nums">
              {formatLakAmount(line.unitLandedCostLak)}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Landed ລາຍການ</p>
            <p className="font-semibold text-sm tabular-nums">
              {formatLakAmount(line.landedCostLak)}
            </p>
          </div>
        </div>
      </div>
    </li>
  );
}

export function ImportOrderLinesPanel({
  lines,
  currency = "CNY",
  canMatch,
  onMatchLine,
  onSkipLine,
}: ImportOrderLinesPanelProps) {
  const pendingCount = lines.filter((line) => line.matchStatus === "pending").length;

  if (lines.length === 0) {
    return (
      <div className="brand-card px-4 py-10 text-center text-muted-foreground text-sm">
        ຍັງບໍ່ມີລາຍການສິນຄ້າ
      </div>
    );
  }

  return (
    <div className="brand-card overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-secondary/15 border-b px-4 py-3">
        <h2 className="font-display text-lg">ລາຍການສິນຄ້າ</h2>
        <span className="text-muted-foreground text-xs tabular-nums">
          {lines.length} ລາຍການ
          {pendingCount > 0 ? ` · ${pendingCount} ລໍຖ້າຈັບຄູ່` : ""}
        </span>
      </div>

      {pendingCount > 0 ? (
        <div className="border-amber-500/20 border-b bg-amber-500/8 px-4 py-2.5 text-amber-950 text-sm dark:text-amber-100">
          {pendingCount} ລາຍການຍັງບໍ່ຈັບຄູ່ SKU — ຈະບໍ່ຖືກຮັບເຂົ້າສະຕັອກຈົນກວ່າຈັບຄູ່
        </div>
      ) : null}

      <ul className="divide-y divide-secondary/15">
        {lines.map((line, index) =>
          isMatchedImportLine(line) ? (
            <MatchedLineCard
              key={line.id}
              line={line}
              index={index}
              currency={currency}
            />
          ) : (
            <PendingLineCard
              key={line.id}
              line={line}
              index={index}
              currency={currency}
              canMatch={canMatch}
              onMatchLine={onMatchLine}
              onSkipLine={onSkipLine}
            />
          ),
        )}
      </ul>
    </div>
  );
}
